/**
 * Mid-flow PHQ-9 / GAD-7 screener prompt, shown when a patient generates their
 * clinician report without a stored mood/anxiety score. They can complete the
 * screeners (scored + persisted via the normal intake save) or skip and
 * generate the report without them. The questions, 0-3 scale, and scoring all
 * come from the shared screener lib so they never drift from the intake step.
 */
import { useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import {
  PHQ9_PROMPT,
  PHQ9_QUESTIONS,
  GAD7_PROMPT,
  GAD7_QUESTIONS,
  SCREENER_FREQUENCY_OPTIONS,
  screenerScore,
} from '@pelvia/shared/lib/patient/screeners'
import { useSaveIntake } from '@pelvia/shared/hooks/patient/useIntake'

const SPRING = { type: 'spring' as const, stiffness: 300, damping: 26 }

// PHQ-9 item 9 (0-indexed 8) screens for thoughts of self-harm. Any non-zero
// answer surfaces crisis resources right away — we asked the question, so we
// own the duty to respond to it.
const PHQ9_SELF_HARM_INDEX = 8

function ScreenerScale({
  value,
  onChange,
}: {
  value: number | null
  onChange: (v: number) => void
}) {
  return (
    <div className="chip-group chip-group-single screener-scale">
      {SCREENER_FREQUENCY_OPTIONS.map(([v, label]) => (
        <button
          key={v}
          type="button"
          className={`chip${value === v ? ' selected' : ''}`}
          onClick={() => onChange(v)}
        >
          {label}
        </button>
      ))}
    </div>
  )
}

export function ScreenerGateModal({
  open,
  onSaved,
  onSkip,
  onCancel,
}: {
  open: boolean
  /** Called after the screener is saved successfully (proceed to report). */
  onSaved: () => void
  /** Called when the patient skips (proceed to report without scores). */
  onSkip: () => void
  /** Called when the patient dismisses the modal (abort report). */
  onCancel: () => void
}) {
  const save = useSaveIntake()
  const [phq9, setPhq9] = useState<(number | null)[]>(() =>
    Array(PHQ9_QUESTIONS.length).fill(null),
  )
  const [gad7, setGad7] = useState<(number | null)[]>(() =>
    Array(GAD7_QUESTIONS.length).fill(null),
  )

  // Toggle behavior mirrors the intake step: tapping the selected chip clears it.
  const setPhq9At = (i: number, v: number) =>
    setPhq9((prev) => {
      const next = [...prev]
      next[i] = next[i] === v ? null : v
      return next
    })
  const setGad7At = (i: number, v: number) =>
    setGad7((prev) => {
      const next = [...prev]
      next[i] = next[i] === v ? null : v
      return next
    })

  const selfHarm = (phq9[PHQ9_SELF_HARM_INDEX] ?? 0) > 0

  async function handleSave() {
    if (save.isPending) return
    try {
      await save.mutateAsync({
        phq9_responses: phq9,
        phq9_score: screenerScore(phq9),
        gad7_responses: gad7,
        gad7_score: screenerScore(gad7),
      })
    } catch {
      // A save hiccup should never block the report the patient came for —
      // fall through; the note renders "Not reported" until the next attempt.
    }
    onSaved()
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="modal-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => !save.isPending && onCancel()}
        >
          <motion.div
            className="modal-panel screener-modal"
            onClick={(e) => e.stopPropagation()}
            initial={{ y: 20, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 20, opacity: 0, scale: 0.96 }}
            transition={SPRING}
          >
            <h3>Quick mood &amp; anxiety check-in</h3>
            <p>
              Your provider report includes the standard depression (PHQ-9) and anxiety
              (GAD-7) screens. You haven&apos;t completed these yet — take a moment now, or
              skip and generate the report without them.
            </p>

            <div className="screener-block">
              <div className="screener-prompt">{PHQ9_PROMPT}</div>
              {PHQ9_QUESTIONS.map((q, i) => (
                <div className="screener-item" key={`phq-${i}`}>
                  <div className="screener-q">
                    {i + 1}. {q}
                  </div>
                  <ScreenerScale value={phq9[i] ?? null} onChange={(v) => setPhq9At(i, v)} />
                </div>
              ))}
            </div>

            {selfHarm && (
              <div className="screener-crisis" role="alert">
                <strong>You&apos;re not alone.</strong> If you&apos;re having thoughts of
                harming yourself, please reach out now:
                <ul>
                  <li>
                    Call or text <strong>988</strong> — Suicide &amp; Crisis Lifeline (24/7)
                  </li>
                  <li>
                    Text <strong>HOME</strong> to <strong>741741</strong> — Crisis Text Line
                  </li>
                  <li>
                    If you&apos;re in immediate danger, call <strong>911</strong>
                  </li>
                </ul>
              </div>
            )}

            <div className="screener-block">
              <div className="screener-prompt">{GAD7_PROMPT}</div>
              {GAD7_QUESTIONS.map((q, i) => (
                <div className="screener-item" key={`gad-${i}`}>
                  <div className="screener-q">
                    {i + 1}. {q}
                  </div>
                  <ScreenerScale value={gad7[i] ?? null} onChange={(v) => setGad7At(i, v)} />
                </div>
              ))}
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onSkip}
                disabled={save.isPending}
              >
                Skip &amp; generate
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={save.isPending}
              >
                {save.isPending ? 'Saving…' : 'Save &amp; generate'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
